import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  CogIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import WorkflowTemplates from '../components/Workflow/WorkflowTemplates';
import WorkflowConfiguration from '../components/Workflow/WorkflowConfiguration';
import ApprovalHistory from '../components/Workflow/ApprovalHistory';
import WorkflowStats from '../components/Workflow/WorkflowStats';

const WorkflowManagement = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [workflowData, setWorkflowData] = useState({
    templates: [],
    stats: {},
    history: []
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const tabs = [
    {
      id: 'templates',
      name: 'Workflow Templates',
      icon: ClipboardDocumentListIcon,
      component: WorkflowTemplates,
      permission: 'view_approval_workflows'
    },
    {
      id: 'configuration',
      name: 'Configuration',
      icon: AdjustmentsHorizontalIcon,
      component: WorkflowConfiguration,
      permission: 'configure_approval_workflows'
    },
    {
      id: 'history',
      name: 'Approval History',
      icon: ChartBarIcon,
      component: ApprovalHistory,
      permission: 'view_approval_history'
    },
    {
      id: 'statistics',
      name: 'Statistics',
      icon: CogIcon,
      component: WorkflowStats,
      permission: 'view_approval_workflows'
    }
  ];

  // Filter tabs based on user permissions
  const visibleTabs = tabs.filter(tab => {
    // For now, show all tabs to admin/hr_manager
    return ['admin', 'hr_manager'].includes(user?.role);
  });

  useEffect(() => {
    loadWorkflowData();
  }, []);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Load workflow templates
      const templatesResponse = await fetch('/api/workflow/templates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const templatesData = await templatesResponse.json();

      // Load workflow statistics (placeholder for now)
      const statsData = {
        totalTemplates: templatesData.data?.length || 0,
        activeWorkflows: 0,
        pendingApprovals: 0,
        averageApprovalTime: '2.3 days'
      };

      setWorkflowData({
        templates: templatesData.data || [],
        stats: statsData,
        history: [] // Will be loaded by individual components
      });

    } catch (error) {
      console.error('Error loading workflow data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    loadWorkflowData();
  };

  const ActiveComponent = visibleTabs.find(tab => tab.id === activeTab)?.component;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Approval Workflow Management</h1>
            <p className="mt-1 text-sm text-gray-500">
              Configure and manage approval workflows for probationary reviews and other processes
            </p>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{workflowData.stats.totalTemplates}</div>
              <div className="text-xs text-gray-500">Templates</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{workflowData.stats.pendingApprovals}</div>
              <div className="text-xs text-gray-500">Pending</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {ActiveComponent && (
            <ActiveComponent
              data={workflowData}
              onDataChange={refreshData}
              user={user}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowManagement;